import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useFindings } from '../lib/queries'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Multi-model consensus (part 83). Each finding is scored by four independent deterministic
 * models. Their votes are weighted by the tenant's trust settings, multiplied by each model's
 * confidence, and tallied with quorum, disagreement and split rules. Every run is stored with
 * its votes, so changing a weight tomorrow does not rewrite yesterday's reasoning.
 */

type Verdict = 'fraud' | 'benign' | 'abstain'
type Outcome = 'fraud' | 'benign' | 'escalated'

interface ConsensusModel {
  code: string
  name_th: string
  description_th: string
  weight: number
  enabled: boolean
  updated_at: string
}

interface ConsensusVote {
  model_code: string
  model_name_th: string
  verdict: Verdict
  confidence: number
  weight: number
  effective_weight: number
  reason_th: string
}

interface ConsensusDecision {
  id: string
  finding_id: string
  rule_code: string
  finding_title: string
  outcome: Outcome
  recommended: 'fraud' | 'benign'
  fraud_score: number
  benign_score: number
  quorum: number
  participants: number
  agreement: number
  disagreement: boolean
  escalated: boolean
  decided_by: string
  tie_break: string
  rationale_th: string
  votes: ConsensusVote[]
  created_at: string
}

interface ModelDraft {
  weight: string
  enabled: boolean
}

const MODELS_KEY = ['consensus-models'] as const
const DECISIONS_KEY = ['consensus-decisions'] as const

const outcomeLabel: Record<Outcome, string> = {
  fraud: 'ทุจริต',
  benign: 'ปกติ',
  escalated: 'ส่งต่อผู้ตรวจสอบ',
}

const outcomeTone: Record<Outcome, string> = {
  fraud: 'sev-critical',
  benign: 'sev-low',
  escalated: 'sev-high',
}

const verdictLabel: Record<Verdict, string> = {
  fraud: 'ทุจริต',
  benign: 'ปกติ',
  abstain: 'ไม่ลงมติ',
}

const verdictTone: Record<Verdict, string> = {
  fraud: 'sev-critical',
  benign: 'sev-low',
  abstain: 'sev-medium',
}

const decidedByLabel: Record<string, string> = {
  weighted: 'ผลรวมถ่วงน้ำหนัก',
  tie_break: 'กฎตัดสินเสมอ',
  split: 'โมเดลแตกความเห็น',
  quorum: 'ไม่ครบองค์ประชุม',
}

const severityLabel: Record<string, string> = {
  low: 'ต่ำ',
  medium: 'ปานกลาง',
  high: 'สูง',
  critical: 'วิกฤต',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function Consensus() {
  const qc = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, ModelDraft>>({})
  const [savingCode, setSavingCode] = useState<string | null>(null)
  const [findingID, setFindingID] = useState('')
  const [quorum, setQuorum] = useState('2')
  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const modelsQ = useQuery({
    queryKey: MODELS_KEY,
    queryFn: () => api<ConsensusModel[]>('/v1/consensus/models'),
  })
  const decisionsQ = useQuery({
    queryKey: DECISIONS_KEY,
    queryFn: () => api<ConsensusDecision[]>('/v1/consensus/decisions?limit=50'),
  })
  const findingsQ = useFindings({ limit: '100' })

  const models = modelsQ.data ?? []
  const decisions = decisionsQ.data ?? []
  const findings = findingsQ.data ?? []

  // Keep the edit form in step with the server without overwriting a draft the operator is
  // still typing: only rows that have no draft yet are seeded.
  useEffect(() => {
    if (models.length === 0) return
    setDrafts((old) => {
      const next = { ...old }
      for (const m of models) {
        if (!next[m.code]) next[m.code] = { weight: String(m.weight), enabled: m.enabled }
      }
      return next
    })
  }, [models])

  useEffect(() => {
    if (!findingID && findings.length > 0) setFindingID(findings[0].id)
  }, [findings, findingID])

  const selected = useMemo(
    () => decisions.find((d) => d.id === selectedID) ?? decisions[0] ?? null,
    [decisions, selectedID],
  )

  const stats = useMemo(() => {
    const enabled = models.filter((m) => m.enabled)
    const weight = enabled.reduce((sum, m) => sum + m.weight, 0)
    const escalated = decisions.filter((d) => d.escalated).length
    const disagreements = decisions.filter((d) => d.disagreement).length
    return { enabled: enabled.length, weight, escalated, disagreements }
  }, [models, decisions])

  const saveModel = useMutation({
    mutationFn: (input: { code: string; weight: number; enabled: boolean }) =>
      apiWrite<ConsensusModel>(`/v1/consensus/models/${encodeURIComponent(input.code)}`, 'PATCH', {
        weight: input.weight,
        enabled: input.enabled,
      }),
    onSuccess: (row) => {
      setActionError(null)
      setNotice(`บันทึกน้ำหนักของ ${row.name_th} เป็น ${row.weight} แล้ว`)
      setDrafts((old) => ({ ...old, [row.code]: { weight: String(row.weight), enabled: row.enabled } }))
      void qc.invalidateQueries({ queryKey: MODELS_KEY })
      setSavingCode(null)
    },
    onError: (err) => {
      setSavingCode(null)
      setActionError(errorText(err, 'บันทึกน้ำหนักโมเดลไม่สำเร็จ'))
    },
  })

  const runConsensus = useMutation({
    mutationFn: (input: { findingID: string; quorum: number }) =>
      apiWrite<ConsensusDecision>(
        `/v1/consensus/findings/${encodeURIComponent(input.findingID)}/run`,
        'POST',
        { quorum: input.quorum },
      ),
    onSuccess: (decision) => {
      setActionError(null)
      setSelectedID(decision.id)
      setNotice(
        `รันฉันทามติแล้ว: ${outcomeLabel[decision.outcome]} · ` +
        `${decision.participants}/${decision.quorum} โมเดลลงมติ · ${decidedByLabel[decision.decided_by] ?? decision.decided_by}`,
      )
      void qc.invalidateQueries({ queryKey: DECISIONS_KEY })
    },
    onError: (err) => setActionError(errorText(err, 'รันฉันทามติไม่สำเร็จ')),
  })

  function saveOne(m: ConsensusModel) {
    const draft = drafts[m.code]
    if (!draft || savingCode) return
    const weight = Number(draft.weight)
    if (!Number.isFinite(weight) || weight < 0 || weight > 10) {
      setActionError('น้ำหนักต้องเป็นตัวเลขตั้งแต่ 0 ถึง 10')
      return
    }
    setSavingCode(m.code)
    saveModel.mutate({ code: m.code, weight, enabled: draft.enabled })
  }

  function run() {
    if (!findingID) {
      setActionError('เลือกข้อสังเกตก่อนรันฉันทามติ')
      return
    }
    const q = Number(quorum)
    if (!Number.isInteger(q) || q < 1 || q > 50) {
      setActionError('องค์ประชุมต้องเป็นจำนวนเต็มตั้งแต่ 1 ถึง 50')
      return
    }
    setNotice(null)
    runConsensus.mutate({ findingID, quorum: q })
  }

  const busy = saveModel.isPending || runConsensus.isPending

  return (
    <>
      <PageHead
        title="ฉันทามติหลายโมเดล"
        sub="ลงคะแนนถ่วงน้ำหนักด้วยน้ำหนักและความมั่นใจของแต่ละโมเดล · องค์ประชุม · ตรวจจับความเห็นต่าง · ตัดสินเสมออย่างกำหนดแน่นอน · เก็บหลักฐานทุกคะแนน"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {decisionsQ.data ? `${decisions.length} การตัดสินล่าสุด` : 'ยังไม่ได้รับข้อมูล'}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={decisionsQ.isFetching || modelsQ.isFetching}
              onClick={() => {
                void qc.invalidateQueries({ queryKey: MODELS_KEY })
                void qc.invalidateQueries({ queryKey: DECISIONS_KEY })
              }}
            >
              {decisionsQ.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
            </button>
          </div>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat
          label="โมเดลที่เปิดใช้งาน"
          value={stats.enabled}
          unit={`/ ${models.length}`}
          foot="ปิดโมเดลเพื่อบันทึกความเห็นแต่ไม่ให้น้ำหนัก"
        />
        <Stat
          label="น้ำหนักรวมที่ใช้ตัดสิน"
          value={stats.weight.toFixed(2)}
          unit="คะแนน"
          foot="น้ำหนักจริงของแต่ละคะแนนคือ น้ำหนัก × ความมั่นใจ"
        />
        <Stat
          label="การตัดสินที่บันทึกไว้"
          value={number(decisions.length)}
          unit="ครั้ง"
          foot="ทุกครั้งเก็บคะแนนของทุกโมเดลแยกเป็นรายการ"
        />
        <Stat
          label="ส่งต่อผู้ตรวจสอบ"
          value={number(stats.escalated)}
          unit="ครั้ง"
          foot={`เห็นต่าง ${stats.disagreements} ครั้ง · องค์ประชุมหรือส่วนต่างไม่พอ`}
        />
      </div>

      {modelsQ.isLoading ? (
        <div className="stack" style={{ marginTop: 18 }}><Skel h={64} /><Skel h={64} /></div>
      ) : modelsQ.isError ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <Empty
            title="โหลดโมเดลไม่สำเร็จ"
            body={errorText(modelsQ.error, 'เรียก /v1/consensus/models ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
          />
        </div>
      ) : (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
            <span className="card-label">น้ำหนักของโมเดล</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              น้ำหนัก 0 หมายถึงบันทึกความเห็นไว้แต่ไม่นับในองค์ประชุม
            </span>
          </div>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 210 }}>รหัสโมเดล</th>
                  <th>ชื่อและวิธีคิด</th>
                  <th style={{ width: 130 }}>น้ำหนัก</th>
                  <th style={{ width: 110 }}>เปิดใช้งาน</th>
                  <th style={{ width: 96 }} />
                </tr>
              </thead>
              <tbody>
                {models.map((m) => {
                  const draft = drafts[m.code] ?? { weight: String(m.weight), enabled: m.enabled }
                  const saving = saveModel.isPending && savingCode === m.code
                  return (
                    <tr key={m.code} style={{ cursor: 'default' }}>
                      <td>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--color-ivory)' }}>{m.code}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                          แก้ไข {thaiDateTime(m.updated_at)}
                        </div>
                      </td>
                      <td>
                        <div style={{ color: 'var(--color-ivory)' }}>{m.name_th}</div>
                        <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{m.description_th}</div>
                      </td>
                      <td>
                        <input
                          className="input tnum"
                          type="number"
                          min={0}
                          max={10}
                          step={0.1}
                          style={{ width: 96, height: 32 }}
                          value={draft.weight}
                          disabled={busy}
                          aria-label={`น้ำหนักของ ${m.name_th}`}
                          onChange={(e) => setDrafts((old) => ({
                            ...old,
                            [m.code]: { ...draft, weight: e.target.value },
                          }))}
                        />
                      </td>
                      <td>
                        <label className="row" style={{ gap: 7, fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={draft.enabled}
                            disabled={busy}
                            onChange={(e) => setDrafts((old) => ({
                              ...old,
                              [m.code]: { ...draft, enabled: e.target.checked },
                            }))}
                          />
                          {draft.enabled ? 'เปิด' : 'ปิด'}
                        </label>
                      </td>
                      <td>
                        <button className="btn btn-sm" disabled={busy} onClick={() => saveOne(m)}>
                          {saving ? 'กำลังบันทึก…' : 'บันทึก'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div className="card-label" style={{ marginBottom: 7 }}>รันฉันทามติกับข้อสังเกต</div>
            <select
              className="input"
              style={{ width: 430, height: 36 }}
              value={findingID}
              onChange={(e) => setFindingID(e.target.value)}
              aria-label="เลือกข้อสังเกต"
            >
              <option value="">เลือกข้อสังเกต</option>
              {findings.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.rule_code} · {f.title} · {severityLabel[f.severity] ?? f.severity}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="card-label" style={{ marginBottom: 7 }}>องค์ประชุมขั้นต่ำ</div>
            <input
              className="input tnum"
              type="number"
              min={1}
              max={50}
              style={{ width: 110, height: 36 }}
              value={quorum}
              onChange={(e) => setQuorum(e.target.value)}
              aria-label="องค์ประชุมขั้นต่ำ"
            />
          </div>
          <button className="btn btn-primary" disabled={busy || !findingID} onClick={run}>
            {runConsensus.isPending ? 'กำลังรัน…' : 'รันฉันทามติ'}
          </button>
          <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto', maxWidth: 380 }}>
            ระบบจะบันทึกการตัดสินใหม่ทุกครั้ง ไม่แก้ไขผลเดิม เมื่อหลักฐานเปลี่ยนให้รันซ้ำเพื่อเทียบ
          </span>
        </div>
      </div>

      {decisionsQ.isLoading ? (
        <div className="stack" style={{ marginTop: 18 }}><Skel h={52} /><Skel h={52} /><Skel h={52} /></div>
      ) : decisionsQ.isError ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <Empty
            title="โหลดผลการตัดสินไม่สำเร็จ"
            body={errorText(decisionsQ.error, 'เรียก /v1/consensus/decisions ไม่สำเร็จ')}
          />
        </div>
      ) : decisions.length === 0 ? (
        <div className="card" style={{ marginTop: 18 }}>
          <Empty
            title="ยังไม่มีการตัดสินด้วยฉันทามติ"
            body="เลือกข้อสังเกตและกด “รันฉันทามติ” ระบบจะให้แต่ละโมเดลลงคะแนน แล้วบันทึกผลพร้อมเหตุผลรายโมเดลไว้ที่นี่"
          />
        </div>
      ) : (
        <>
          <div className="card" style={{ marginTop: 18 }}>
            <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
              <span className="card-label">ผลการตัดสินล่าสุด</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>ใหม่สุดก่อน · เลือกแถวเพื่อดูคะแนนรายโมเดล</span>
            </div>
            <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>ข้อสังเกต</th>
                    <th style={{ width: 150 }}>ผล</th>
                    <th style={{ width: 150 }} className="num">คะแนน ทุจริต / ปกติ</th>
                    <th style={{ width: 108 }} className="num">องค์ประชุม</th>
                    <th style={{ width: 160 }}>วิธีตัดสิน</th>
                    <th style={{ width: 160 }}>เวลา</th>
                    <th style={{ width: 64 }} />
                  </tr>
                </thead>
                <tbody>
                  {decisions.map((d) => {
                    const active = selected?.id === d.id
                    return (
                      <tr
                        key={d.id}
                        onClick={() => setSelectedID(d.id)}
                        style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                      >
                        <td>
                          <div style={{ color: 'var(--color-ivory)' }}>{d.finding_title}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>{d.rule_code}</div>
                        </td>
                        <td>
                          <span className={`sev ${outcomeTone[d.outcome]}`}>
                            <i className="sev-dot" />
                            {outcomeLabel[d.outcome]}
                          </span>
                          {d.escalated ? (
                            <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                              ข้อเสนอ: {verdictLabel[d.recommended]}
                            </div>
                          ) : null}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>
                          {d.fraud_score.toFixed(2)} / {d.benign_score.toFixed(2)}
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{d.participants} / {d.quorum}</td>
                        <td className="dim" style={{ fontSize: 11.5 }}>
                          {decidedByLabel[d.decided_by] ?? d.decided_by}
                          {d.tie_break ? <div className="mono" style={{ fontSize: 10.5, marginTop: 3 }}>{d.tie_break}</div> : null}
                        </td>
                        <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(d.created_at)}</td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedID(d.id)}>ดู</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {selected ? (
            <div className="card card-pad" style={{ marginTop: 18 }}>
              <div className="row" style={{ gap: 12, marginBottom: 12 }}>
                <span className={`sev ${outcomeTone[selected.outcome]}`}>
                  <i className="sev-dot" />
                  {outcomeLabel[selected.outcome]}
                </span>
                <span className="dim" style={{ fontSize: 12 }}>
                  {selected.participants} จาก {selected.quorum} โมเดลลงมติ · ความเห็นพ้อง {(selected.agreement * 100).toFixed(1)}%
                  {selected.disagreement ? ' · มีความเห็นต่าง' : ''}
                </span>
                <span className="dim mono" style={{ marginLeft: 'auto', fontSize: 10.5 }}>{selected.id}</span>
              </div>
              <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.85, marginBottom: 16 }}>
                {selected.rationale_th}
              </p>

              <div className="grid-2" style={{ marginBottom: 16 }}>
                <div className="card card-pad" style={{ background: 'transparent' }}>
                  <div className="card-label">คะแนนฝ่ายทุจริต</div>
                  <div className="stat-value tnum">{selected.fraud_score.toFixed(4)}</div>
                </div>
                <div className="card card-pad" style={{ background: 'transparent' }}>
                  <div className="card-label">คะแนนฝ่ายปกติ</div>
                  <div className="stat-value tnum">{selected.benign_score.toFixed(4)}</div>
                </div>
              </div>

              <div className="card-label" style={{ marginBottom: 10 }}>คะแนนและเหตุผลรายโมเดล</div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 220 }}>โมเดล</th>
                      <th style={{ width: 110 }}>ความเห็น</th>
                      <th style={{ width: 110 }} className="num">ความมั่นใจ</th>
                      <th style={{ width: 92 }} className="num">น้ำหนัก</th>
                      <th style={{ width: 110 }} className="num">น้ำหนักมีผล</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.votes.map((v) => (
                      <tr key={v.model_code} style={{ cursor: 'default' }}>
                        <td>
                          <div style={{ color: 'var(--color-ivory)' }}>{v.model_name_th}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>{v.model_code}</div>
                        </td>
                        <td>
                          <span className={`sev ${verdictTone[v.verdict]}`}>
                            <i className="sev-dot" />
                            {verdictLabel[v.verdict]}
                          </span>
                        </td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{(v.confidence * 100).toFixed(1)}%</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{v.weight.toFixed(2)}</td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{v.effective_weight.toFixed(4)}</td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{v.reason_th}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}
